;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();export type ClientSession = {
  user?: {
    idToken?: string | null
    expiresAt?: number
    userId?: string | number | null
    sub?: string
    error?: string
    orgId?: string | number | null
    providerId?: number | null
    authRole?: string | null
  }
} | null

export type ClientSessionUpdate = {
  forceRefresh?: boolean
  orgId?: string | number | null
}
type SessionStatus = "loading" | "authenticated" | "unauthenticated"
type SessionUpdater = (
  payload?: ClientSessionUpdate,
) => Promise<ClientSession | undefined>

function getAccountSubject(value: ClientSession) {
  const user = value?.user
  if (!user) return null
  if (user.sub) return `sub:${user.sub}`
  return user.userId != null && String(user.userId) !== ""
    ? `user:${user.userId}`
    : null
}

export function getSessionIdentity(value: ClientSession) {
  return value?.user?.idToken && !value.user.error ? getAccountSubject(value) : null
}

export function getSessionResourceScope(value: ClientSession) {
  const identity = getSessionIdentity(value)
  const user = value?.user
  return identity
    ? JSON.stringify([
        identity,
        user.orgId == null ? null : String(user.orgId),
        user.providerId ?? null,
        user.authRole ?? null,
      ])
    : null
}

export class AuthenticationRequiredError extends Error {
  constructor() {
    super("Your session has expired. Please sign in again.")
    this.name = "AuthenticationRequiredError"
  }
}

export function createClientSession(
  initialSession: ClientSession,
  update: SessionUpdater,
  onExpired: () => void,
) {
  let session = initialSession
  let updateProvider = update
  let refreshPromise: Promise<ClientSession> | null = null
  let generation = 0
  let expired = false
  const listeners = new Set<() => void>()
  const providerWaiters = new Set<() => void>()
  const discardedResults = new WeakSet<NonNullable<ClientSession>>()
  let providerManaged = false
  let providerStatus: SessionStatus = "authenticated"
  let providerRevision = 0
  type UpdateTask = {
    payload?: ClientSessionUpdate
    generation: number
    identity: string | null
    cancellation: AbortController
    promise: Promise<ClientSession>
    resolve: (session: ClientSession) => void
    reject: (error: unknown) => void
  }
  const queue: UpdateTask[] = []
  let active: UpdateTask | null = null
  const sessionChanged = () => new DOMException("Session changed", "AbortError")
  // NextAuth publishes update results into React state itself, before our
  // promise consumer can reject them. Filter late results at both boundaries.
  const isCurrentSession = (next: ClientSession) =>
    !next ||
    (!expired &&
      !discardedResults.has(next) &&
      !(
        active &&
        active.generation !== generation &&
        getAccountSubject(next) === active.identity
      ))

  const setSession = (next: ClientSession) => {
    // Expiration navigates to server logout. A later NextAuth render must not
    // restore credentials while that navigation is still in progress.
    if (expired && next) return
    const changed = getSessionIdentity(session) !== getSessionIdentity(next)
    session = next
    if (changed) {
      generation += 1
      refreshPromise = null
      for (const task of [...(active ? [active] : []), ...queue.splice(0)]) {
        task.cancellation.abort()
        task.reject(sessionChanged())
      }
      for (const listener of listeners) listener()
    }
  }

  const expire = () => {
    if (expired) return
    expired = true
    setSession(null)
    onExpired()
  }

  const assertCurrent = (task: UpdateTask) => {
    if (task.generation !== generation || task.cancellation.signal.aborted)
      throw sessionChanged()
  }

  // NextAuth's update promise can settle before React commits its ready state.
  // Wait for that commit so the next update doesn't call a stale, busy closure.
  const waitUntilReady = async (task: UpdateTask, afterRevision = -1) => {
    assertCurrent(task)
    while (
      providerManaged &&
      (providerStatus === "loading" || providerRevision <= afterRevision)
    ) {
      await new Promise<void>((resolve) => {
        const wake = () => {
          providerWaiters.delete(wake)
          task.cancellation.signal.removeEventListener("abort", wake)
          resolve()
        }
        providerWaiters.add(wake)
        task.cancellation.signal.addEventListener("abort", wake, { once: true })
      })
      assertCurrent(task)
    }
  }

  const drain = async () => {
    if (active || !queue.length) return
    const task = queue.shift()!
    active = task
    try {
      await waitUntilReady(task)
      for (;;) {
        assertCurrent(task)
        const beforeUpdate = providerRevision
        const next = await updateProvider(task.payload)
        if (task.generation !== generation && next) discardedResults.add(next)
        assertCurrent(task)
        if (next === undefined) {
          // An external NextAuth update may have started before its loading
          // render committed. Wait for it, then apply this task's own payload.
          if (!providerManaged) throw new Error("Session provider is not ready")
          await waitUntilReady(task, beforeUpdate)
          continue
        }
        if (!next?.user?.idToken || next.user.error) {
          throw new AuthenticationRequiredError()
        }
        await waitUntilReady(task, beforeUpdate)
        setSession(next)
        assertCurrent(task)
        task.resolve(next)
        break
      }
    } catch (error) {
      if (
        task.generation === generation &&
        error instanceof AuthenticationRequiredError
      )
        expire()
      task.reject(error)
    } finally {
      active = null
      void drain()
    }
  }

  const updateSession = (payload?: ClientSessionUpdate) => {
    if (!session?.user?.idToken || session.user.error) {
      expire()
      return Promise.reject(new AuthenticationRequiredError())
    }
    let resolve!: (session: ClientSession) => void
    let reject!: (error: unknown) => void
    const promise = new Promise<ClientSession>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise
      reject = rejectPromise
    })
    queue.push({
      payload,
      generation,
      identity: getAccountSubject(session),
      cancellation: new AbortController(),
      promise,
      resolve,
      reject,
    })
    void drain()
    return promise
  }

  const refresh = () => {
    if (refreshPromise) return refreshPromise
    const lastUpdate = queue.at(-1) ?? active
    // Automatic recovery can join a queued/in-progress refresh, but explicit
    // post-save updates always enqueue their own read of the updated account.
    const pending =
      lastUpdate?.generation === generation && lastUpdate.payload?.forceRefresh
        ? lastUpdate.promise
        : updateSession({ forceRefresh: true })
    refreshPromise = pending
    const settled = () => {
      if (refreshPromise === pending) refreshPromise = null
    }
    void pending.then(settled, settled)
    return pending
  }

  const waitForUpdates = async () => {
    let pending: UpdateTask | null | undefined
    while ((pending = queue.at(-1) ?? active)) {
      if (pending.generation !== generation) return
      await pending.promise
    }
  }

  return {
    setSession,
    setAuthState(
      next: ClientSession,
      status: SessionStatus,
      updater: SessionUpdater,
    ) {
      providerManaged = true
      providerStatus = status
      updateProvider = updater
      providerRevision += 1
      if (status !== "loading" && isCurrentSession(next)) {
        setSession(status === "unauthenticated" ? null : next)
        if (next?.user?.error) expire()
      }
      for (const wake of [...providerWaiters]) wake()
    },
    getSession: () => session,
    isCurrentSession,
    getGeneration: () => generation,
    expire,
    refresh,
    updateSession,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    async getToken() {
      await waitForUpdates()
      if (session?.user?.error) {
        expire()
        throw new AuthenticationRequiredError()
      }
      if (
        session?.user?.expiresAt &&
        Date.now() >= session.user.expiresAt - 30_000
      ) {
        await refresh()
      }
      await waitForUpdates()
      return session?.user?.idToken ?? null
    },
  }
}

export type ClientSessionController = ReturnType<typeof createClientSession>
